import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, Award, Loader2 } from "lucide-react";
import api from "@/lib/api";

const daysLeft = (expiry) => {
  if (!expiry) return null;
  try {
    const today = new Date(new Date().toLocaleDateString("en-CA", { timeZone: "America/Toronto" }) + "T12:00:00");
    const exp = new Date(`${String(expiry).slice(0, 10)}T12:00:00`);
    return Math.round((exp - today) / 86400000);
  } catch { return null; }
};

const expiryChip = (expiry) => {
  const d = daysLeft(expiry);
  if (d === null) return { cls: "border-white/15 text-white/50", label: "no expiry" };
  if (d < 0) return { cls: "border-red-500/60 bg-red-500/15 text-red-700 dark:text-red-300", label: `expired ${Math.abs(d)}d ago` };
  if (d <= 7) return { cls: "border-red-500/60 bg-red-500/15 text-red-700 dark:text-red-300", label: `${d}d left` };
  if (d <= 30) return { cls: "border-amber-500/60 bg-amber-500/15 text-amber-800 dark:text-amber-300", label: `${d}d left` };
  return { cls: "border-emerald-500/50 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300", label: `${d}d left` };
};

/**
 * AdminCertificatesDialog — read-only view of one employee's certificate
 * wallet from the Employees list. Click a thumbnail for the full image.
 */
const AdminCertificatesDialog = ({ employee, onClose }) => {
  const [certs, setCerts] = useState(null);
  const [thumbs, setThumbs] = useState({});
  const [viewer, setViewer] = useState(null); // cert being viewed large

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/employees/${employee.employee_id}/certificates`);
        setCerts(Array.isArray(data) ? data : []);
      } catch { setCerts([]); }
    })();
  }, [employee.employee_id]);

  useEffect(() => {
    if (!certs) return;
    let cancelled = false;
    (async () => {
      for (const c of certs) {
        if (thumbs[c.cert_id]) continue;
        try {
          const { data } = await api.get(`/me/certificates/${c.cert_id}/image`, { responseType: "blob" });
          if (cancelled) return;
          setThumbs((t) => ({ ...t, [c.cert_id]: URL.createObjectURL(data) }));
        } catch { /* skip */ }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [certs]);

  return createPortal(
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 p-3" data-testid="admin-certs-dialog">
      <div className="w-full max-w-lg overflow-hidden rounded-lg border border-white/10 bg-[#0b0b0f] text-white">
        <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
          <div className="min-w-0">
            <h3 className="truncate text-lg font-bold uppercase tracking-tight" data-testid="admin-certs-title">
              Certificates — {employee.name}
            </h3>
            <p className="font-mono text-[10px] uppercase tracking-widest text-white/45">Tap a card to view the image</p>
          </div>
          <button onClick={onClose} aria-label="Close" data-testid="admin-certs-close" className="rounded border border-white/15 bg-white/5 p-1.5 text-white/70"><X className="h-4 w-4" /></button>
        </div>
        <div className="max-h-[70vh] overflow-y-auto p-4">
          {certs === null ? (
            <div className="flex justify-center py-8 text-white/50"><Loader2 className="h-5 w-5 animate-spin" /></div>
          ) : certs.length === 0 ? (
            <p className="py-6 text-center font-mono text-[10px] uppercase tracking-widest text-white/40" data-testid="admin-certs-empty">
              No certificates uploaded
            </p>
          ) : (
            <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3" data-testid="admin-certs-grid">
              {certs.map((c) => {
                const chip = expiryChip(c.expiry_date);
                return (
                  <li key={c.cert_id}>
                    <button
                      type="button"
                      onClick={() => setViewer(c)}
                      data-testid={`admin-cert-${c.cert_id}`}
                      className="w-full overflow-hidden rounded-lg border border-white/10 bg-white/[0.03] text-left hover:border-[#1a3fd1]"
                    >
                      <div className="relative h-24 w-full bg-black/40">
                        {thumbs[c.cert_id] ? (
                          <img src={thumbs[c.cert_id]} alt={c.name} className="h-full w-full object-cover" />
                        ) : (
                          <div className="flex h-full w-full items-center justify-center text-white/25"><Award className="h-6 w-6" /></div>
                        )}
                        <span className={`absolute bottom-1 right-1 rounded border px-1.5 py-0.5 font-mono text-[9px] font-bold uppercase ${chip.cls}`}>
                          {chip.label}
                        </span>
                      </div>
                      <div className="px-2 py-1.5">
                        <p className="truncate text-xs font-semibold text-white/90">{c.name}</p>
                        <p className="truncate font-mono text-[9px] text-white/45">
                          {c.expiry_date ? `exp ${c.expiry_date}` : "no expiry"}
                        </p>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      {viewer && (
        <div className="fixed inset-0 z-[85] flex items-center justify-center bg-black/85 p-4" onClick={() => setViewer(null)} data-testid="admin-cert-viewer">
          <div className="max-h-full max-w-3xl overflow-auto rounded-lg border border-white/10 bg-[#0b0b0f] p-3" onClick={(e) => e.stopPropagation()}>
            <div className="mb-2 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-white">{viewer.name}</p>
                {viewer.description && <p className="truncate text-xs text-white/50">{viewer.description}</p>}
              </div>
              <button onClick={() => setViewer(null)} aria-label="Close viewer" className="rounded border border-white/15 bg-white/5 p-1.5 text-white/70"><X className="h-4 w-4" /></button>
            </div>
            {thumbs[viewer.cert_id] && (
              <img src={thumbs[viewer.cert_id]} alt={viewer.name} className="max-h-[70vh] w-auto max-w-full rounded object-contain" />
            )}
          </div>
        </div>
      )}
    </div>,
    document.body,
  );
};

export default AdminCertificatesDialog;
